import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { TableRow, TableCell } from '@/components/ui/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronRight, Check, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EnumPopover } from './EnumPopover';
import { EnhancedSchemaField } from '@/lib/data-standards/schemas';
import { getTypeColors, getFormatColors, FIELD_TYPE_COLORS } from '@/lib/data-standards/field-utils';

interface FieldRowProps {
  field: EnhancedSchemaField;
  depth?: number;
  schema?: object;
}

export function FieldRow({ field, depth = 0, schema }: FieldRowProps) {
  const [isOpen, setIsOpen] = useState(false);

  const hasExpandableContent =
    field.nestedFields?.length ||
    field.constraints?.minLength ||
    field.constraints?.maxLength ||
    field.constraints?.minimum ||
    field.constraints?.maximum ||
    field.constraints?.pattern ||
    field.example !== undefined;

  const typeColors = getTypeColors(field.type);
  const formatColors = field.format ? getFormatColors(field.format) : null;

  const constraintsList: string[] = [];
  if (field.constraints?.minLength !== undefined) {
    constraintsList.push(`minLength: ${field.constraints.minLength}`);
  }
  if (field.constraints?.maxLength !== undefined) {
    constraintsList.push(`maxLength: ${field.constraints.maxLength}`);
  }
  if (field.constraints?.minimum !== undefined) {
    constraintsList.push(`min: ${field.constraints.minimum}`);
  }
  if (field.constraints?.maximum !== undefined) {
    constraintsList.push(`max: ${field.constraints.maximum}`);
  }
  if (field.constraints?.pattern) {
    constraintsList.push(`pattern: ${field.constraints.pattern}`);
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild disabled={!hasExpandableContent}>
        <TableRow
          className={cn(
            'group transition-colors',
            hasExpandableContent && 'cursor-pointer hover:bg-muted/50',
            isOpen && 'bg-muted/30'
          )}
        >
          {/* Field Name */}
          <TableCell
            className="font-mono text-sm py-2"
            style={{ paddingLeft: `${depth * 24 + 12}px` }}
          >
            <div className="flex items-center gap-2">
              {hasExpandableContent && (
                <ChevronRight
                  className={cn(
                    'h-4 w-4 text-muted-foreground transition-transform shrink-0',
                    isOpen && 'rotate-90'
                  )}
                />
              )}
              {!hasExpandableContent && <div className="w-4" />}
              <span className={cn(depth > 0 && 'text-muted-foreground')}>
                {field.name}
              </span>
            </div>
          </TableCell>

          {/* Type */}
          <TableCell className="py-2">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Badge
                variant="outline"
                className={cn(
                  'font-mono text-xs border',
                  typeColors.bg,
                  typeColors.text,
                  typeColors.border
                )}
              >
                {field.baseType || field.type}
              </Badge>
              {field.format && formatColors && (
                <Link to={`/docs/data-types#${field.format}`}>
                  <Badge
                    variant="outline"
                    className={cn('text-xs cursor-pointer hover:opacity-80', formatColors.bg, formatColors.text)}
                  >
                    {field.format}
                  </Badge>
                </Link>
              )}
              {field.enumValues && field.enumValues.length > 0 && (
                <EnumPopover values={field.enumValues} fieldName={field.name} />
              )}
            </div>
          </TableCell>

          {/* Required */}
          <TableCell className="text-center py-2">
            {field.required ? (
              <Check className="h-4 w-4 text-green-500 mx-auto" />
            ) : (
              <Minus className="h-4 w-4 text-muted-foreground mx-auto" />
            )}
          </TableCell>

          {/* Description */}
          <TableCell className="text-sm text-muted-foreground py-2">
            <span className="line-clamp-2">{field.description}</span>
          </TableCell>
        </TableRow>
      </CollapsibleTrigger>

      {hasExpandableContent && (
        <CollapsibleContent asChild>
          <tr>
            <td colSpan={4} className="p-0">
              <div
                className="bg-muted/20 border-t border-b py-2 px-3 space-y-2"
                style={{ marginLeft: `${depth * 24 + 12}px` }}
              >
                {/* Constraints */}
                {constraintsList.length > 0 && (
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-medium text-muted-foreground w-20 shrink-0">
                      Constraints:
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {constraintsList.map((constraint, idx) => (
                        <Badge
                          key={idx}
                          variant="secondary"
                          className="text-xs font-mono"
                        >
                          {constraint}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Example */}
                {field.example !== undefined && (
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-medium text-muted-foreground w-20 shrink-0">
                      Example:
                    </span>
                    <code className="text-xs bg-muted px-2 py-1 rounded font-mono break-all">
                      {typeof field.example === 'object'
                        ? JSON.stringify(field.example)
                        : String(field.example)}
                    </code>
                  </div>
                )}

                {/* Nested Fields */}
                {field.nestedFields && field.nestedFields.length > 0 && (
                  <div className="space-y-1 pt-2">
                    <span className="text-xs font-medium text-muted-foreground">
                      Nested Fields:
                    </span>
                    <div className="border rounded-md overflow-hidden bg-background">
                      <table className="w-full text-sm table-fixed">
                        <tbody>
                          {field.nestedFields.map((nestedField) => (
                            <NestedFieldRow
                              key={nestedField.name}
                              field={nestedField}
                              depth={0}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </td>
          </tr>
        </CollapsibleContent>
      )}
    </Collapsible>
  );
}

// Simplified nested field row for recursive display
interface NestedFieldRowProps {
  field: EnhancedSchemaField;
  depth: number;
}

function NestedFieldRow({ field, depth }: NestedFieldRowProps) {
  const [isOpen, setIsOpen] = useState(false);
  const hasNested = field.nestedFields && field.nestedFields.length > 0;

  const typeColors = getTypeColors(field.type);

  return (
    <>
      <tr
        className={cn(
          'border-b last:border-b-0',
          hasNested && 'cursor-pointer hover:bg-muted/50'
        )}
        onClick={() => hasNested && setIsOpen(!isOpen)}
      >
        <td
          className="py-2 font-mono text-xs"
          style={{ paddingLeft: `${depth * 16 + 12}px` }}
        >
          <div className="flex items-center gap-1.5">
            {hasNested && (
              <ChevronRight
                className={cn(
                  'h-3 w-3 text-muted-foreground transition-transform',
                  isOpen && 'rotate-90'
                )}
              />
            )}
            {!hasNested && <span className="w-3" />}
            <span>{field.name}</span>
          </div>
        </td>
        <td className="py-2 w-[20%] min-w-[100px]">
          <div className="flex items-center gap-1">
            <Badge
              variant="outline"
              className={cn(
                'text-xs font-mono',
                typeColors.bg,
                typeColors.text,
                typeColors.border
              )}
            >
              {field.baseType || field.type}
            </Badge>
            {field.enumValues && (
              <EnumPopover values={field.enumValues} fieldName={field.name}>
                <Badge
                  variant="outline"
                  className="text-xs cursor-pointer bg-pink-500/10 text-pink-600 dark:text-pink-400 border-pink-500/20"
                >
                  {field.enumValues.length}
                </Badge>
              </EnumPopover>
            )}
          </div>
        </td>
        <td className="py-2 w-[10%] min-w-[50px] text-center">
          {field.required ? (
            <Check className="h-3 w-3 text-green-500 mx-auto" />
          ) : (
            <Minus className="h-3 w-3 text-muted-foreground mx-auto" />
          )}
        </td>
        <td className="py-2 w-[45%] text-xs text-muted-foreground pr-3">
          <span className="line-clamp-1">{field.description}</span>
        </td>
      </tr>
      {isOpen &&
        hasNested &&
        field.nestedFields!.map((nestedField) => (
          <NestedFieldRow
            key={nestedField.name}
            field={nestedField}
            depth={depth + 1}
          />
        ))}
    </>
  );
}
